import { LangLink } from "@/components/LangLink";
import { useLanguage } from "@/lib/language";
import type { InsightMeta } from "@/components/insights/InsightShell";
import { H2, P, TableWrap, thClass, tdClass } from "@/components/insights/prose";

export const iso29001VsApiQ1Meta: InsightMeta = {
  slug: "iso-29001-vs-api-q1",
  metaTitle: {
    en: "ISO 29001 vs API Q1 for Oil & Gas | IraqCert",
    ar: "ايزو 29001 مقابل API Q1 للنفط والغاز | عراق سيرت",
  },
  title: {
    en: "ISO 29001 vs API Q1: Which Does Your Oil & Gas Company Need?",
    ar: "ايزو 29001 مقابل API Q1: أيهما تحتاج شركتك النفطية؟",
  },
  description: {
    en: "Compare ISO 29001, API Q1 and API Q2 by scope, certification route and the buyer’s written requirement, without assuming they are interchangeable.",
    ar: "قارن ISO 29001 وAPI Q1 وAPI Q2 من حيث النطاق ومسار الشهادة ومتطلب المشتري المكتوب، دون افتراض أنها بدائل متكافئة.",
  },
  datePublished: "2026-07-21",
  dateModified: "2026-10-09",
  dateLabel: { en: "9 October 2026", ar: "9 تشرين الأول 2026" },
  faq: [
    {
      q: { en: "Is ISO 29001 the same as ISO 9001?", ar: "هل ايزو 29001 نفسه ايزو 9001؟" },
      a: {
        en: "No. ISO 29001 is built on ISO 9001 but adds sector-specific requirements for the petroleum, petrochemical, and natural gas industry, focused on defect prevention and supply-chain risk.",
        ar: "لا. يُبنى ايزو 29001 على ايزو 9001 لكنه يضيف متطلبات خاصة بقطاع النفط والبتروكيماويات والغاز الطبيعي، تركّز على منع العيوب ومخاطر سلسلة التوريد.",
      },
    },
    {
      q: { en: "Do I need both ISO 29001 and API Q1?", ar: "هل أحتاج ايزو 29001 و API Q1 معاً؟" },
      a: {
        en: "The contract decides. ISO 29001 and API Q1 are distinct documents; holding one does not automatically satisfy a requirement for the other. Confirm the requested edition and certification or registration route.",
        ar: "العقد هو الذي يحدد ذلك. ISO 29001 وAPI Q1 وثيقتان مختلفتان؛ ولا يحقق الحصول على إحداهما تلقائياً شرط الأخرى. أكّد الإصدار المطلوب ومسار الشهادة أو التسجيل.",
      },
    },
    {
      q: {
        en: "What is the difference between API Q1 and API Q2?",
        ar: "ما الفرق بين API Q1 و API Q2؟",
      },
      a: {
        en: "API Q1’s tenth edition covers organizations providing products, including certain non-manufacturing activities. API Q2 addresses service supply organizations. Match your actual activities to the relevant scope and contract.",
        ar: "يشمل الإصدار العاشر من API Q1 مؤسسات تقدم منتجات، بما فيها بعض الأنشطة غير التصنيعية. ويختص API Q2 بمؤسسات تقديم الخدمات. طابق أنشطتك الفعلية مع النطاق المعني والعقد.",
      },
    },
    {
      q: {
        en: "Which do oil companies in Iraq require?",
        ar: "أيهما تشترط شركات النفط في العراق؟",
      },
      a: {
        en: "Check the specific tender or prequalification instructions. Neither a sector label nor another supplier’s certificate establishes what your buyer will accept.",
        ar: "راجع تعليمات المناقصة أو التأهيل المسبق المحددة. لا يثبت اسم القطاع أو شهادة مورّد آخر ما سيقبله المشتري الذي تستهدفه.",
      },
    },
  ],
};

const compare = [
  {
    point: { en: "Standard publisher", ar: "ناشر المواصفة" },
    a29001: { en: "ISO", ar: "منظمة الأيزو" },
    apiq1: { en: "American Petroleum Institute (API)", ar: "معهد البترول الأمريكي (API)" },
  },
  {
    point: { en: "Relationship to ISO 9001", ar: "العلاقة بـ ISO 9001" },
    a29001: { en: "ISO 9001", ar: "ايزو 9001" },
    apiq1: {
      en: "Distinct API specification; tenth edition aligned with ISO 9001:2015",
      ar: "مواصفة مستقلة من API؛ يتواءم إصدارها العاشر مع ISO 9001:2015",
    },
  },
  {
    point: { en: "Applies to", ar: "ينطبق على" },
    a29001: {
      en: "Product and service suppliers to petroleum, petrochemical and natural gas industries",
      ar: "موردو المنتجات والخدمات لصناعات النفط والبتروكيماويات والغاز الطبيعي",
    },
    apiq1: {
      en: "Product providers, including specified non-manufacturing activities",
      ar: "مقدمو المنتجات، بما يشمل أنشطة غير تصنيعية محددة",
    },
  },
  {
    point: { en: "Certification route", ar: "مسار الشهادة" },
    a29001: {
      en: "Independent certification body; verify its relevant accreditation scope",
      ar: "جهة منح مستقلة؛ تحقّق من نطاق اعتمادها ذي الصلة",
    },
    apiq1: {
      en: "APIQR registration; API Monogram product licensing is a separate programme",
      ar: "تسجيل APIQR؛ وترخيص علامة API Monogram للمنتجات برنامج مستقل",
    },
  },
];

export function Iso29001VsApiQ1Body() {
  const { t } = useLanguage();
  return (
    <>
      <P>
        {t({
          en: "ISO 29001 and API Q1 are separate quality-management documents for the oil and gas supply chain. Their scopes and assessment routes differ, so do not treat the certificates or registrations as interchangeable. ISO publishes standards; it does not certify companies or accredit certification bodies. For preparation support, see ",
          ar: "ISO 29001 وAPI Q1 وثيقتان منفصلتان لإدارة الجودة في سلسلة توريد النفط والغاز. تختلف نطاقاتهما ومسارات تقييمهما، فلا تتعامل مع الشهادات أو التسجيلات باعتبارها بدائل متكافئة. تنشر ISO المواصفات، ولا تمنح شهادات للشركات ولا تعتمد جهات منح الشهادات. لدعم التحضير، راجع ",
        })}
        <LangLink to="/services/iso-29001" className="font-semibold text-primary hover:underline">
          {t({ en: "ISO 29001 certification", ar: "شهادة ايزو 29001" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>{t({ en: "What is ISO 29001?", ar: "ما هو ايزو 29001؟" })}</H2>
      <P>
        {t({
          en: "ISO 29001:2020 supplements ISO 9001:2015 for product and service suppliers in petroleum, petrochemicals and natural gas. It adds sector requirements and guidance addressing supply-chain risks and opportunities. ISO’s catalogue also lists Amendment 1:2024; confirm the applicable documents with the certification body.",
          ar: "تكمل ISO 29001:2020 مواصفة ISO 9001:2015 لموردي المنتجات والخدمات في النفط والبتروكيماويات والغاز الطبيعي. وتضيف متطلبات وإرشادات قطاعية تتناول مخاطر سلسلة التوريد وفرصها. ويسرد دليل ISO أيضاً التعديل 1:2024؛ فأكّد الوثائق المنطبقة مع الجهة المانحة.",
        })}
      </P>

      <H2>{t({ en: "What is API Q1 (and API Q2)?", ar: "ما هو API Q1 (و API Q2)؟" })}</H2>
      <P>
        {t({
          en: "API Q1’s tenth edition aligns with ISO 9001:2015 but is a separate specification. API broadened its scope beyond manufacturing to include activities such as engineering, design, inspection and product-related distribution. API Q2’s second edition addresses service supply organizations. Read the applicable scope before selecting a route; the label “service company” alone does not settle the choice.",
          ar: "يتواءم الإصدار العاشر من API Q1 مع ISO 9001:2015، لكنه مواصفة مستقلة. وسّعت API نطاقه ليشمل، إلى جانب التصنيع، أنشطة مثل الهندسة والتصميم والفحص وتوزيع المنتجات. ويختص الإصدار الثاني من API Q2 بمؤسسات تقديم الخدمات. اقرأ النطاق المنطبق قبل اختيار المسار؛ فتسمية «شركة خدمات» وحدها لا تحسم الاختيار.",
        })}
      </P>
      <TableWrap>
        <thead className="bg-accent">
          <tr>
            <th className={thClass}>{t({ en: "Point", ar: "المعيار" })}</th>
            <th className={thClass}>{t({ en: "ISO 29001", ar: "ايزو 29001" })}</th>
            <th className={thClass}>{t({ en: "API Q1", ar: "API Q1" })}</th>
          </tr>
        </thead>
        <tbody>
          {compare.map((row) => (
            <tr key={row.point.en} className="border-t border-border">
              <td className={`${tdClass} font-semibold text-foreground`}>{t(row.point)}</td>
              <td className={tdClass}>{t(row.a29001)}</td>
              <td className={tdClass}>{t(row.apiq1)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <H2>
        {t({
          en: "Which one do Iraqi oil & gas vendors need?",
          ar: "أيهما يحتاج موردو النفط والغاز في العراق؟",
        })}
      </H2>
      <P>
        {t({
          en: "Ask for the exact standard, edition, activity and evidence required. APIQR management-system registration and an API Monogram licence for eligible products are different programmes; an ISO 29001 certificate does not confer either. IraqCert confirms the proposed ISO 29001 issuing body and accreditation scope before booking: ISO 29001 is outside AQSR’s disclosed UAF-accredited scope. Certification does not guarantee supplier approval. IraqCert also supports ",
          ar: "اطلب تحديد المواصفة والإصدار والنشاط والأدلة المطلوبة بدقة. تسجيل نظام الإدارة لدى APIQR وترخيص علامة API Monogram للمنتجات المؤهلة برنامجان مختلفان؛ ولا تمنح شهادة ISO 29001 أياً منهما. تؤكد عراق سيرت الجهة المانحة المقترحة ونطاق اعتمادها قبل الحجز؛ فمعيار ISO 29001 خارج نطاق اعتماد UAF المعلن لشركة AQSR. لا تضمن الشهادة قبول المورّد. وتدعم عراق سيرت أيضاً ",
        })}
        <LangLink
          to="/services/oil-gas-registration"
          className="font-semibold text-primary hover:underline"
        >
          {t({ en: "oil and gas vendor registration", ar: "تسجيل موردي النفط والغاز" })}
        </LangLink>
        {t({ en: ".", ar: "." })}
      </P>

      <H2>{t({ en: "Sources", ar: "المصادر" })}</H2>
      <P>
        <a
          href="https://www.iso.org/standard/67773.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "ISO: ISO 29001:2020 scope and amendment",
            ar: "ISO: نطاق ISO 29001:2020 وتعديلها",
          })}
        </a>
      </P>
      <P>
        <a
          href="https://www.api.org/products-and-services/standards/important-standards-announcements/pressreleasespecq1-10thed"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({
            en: "API: Q1 tenth edition and expanded scope",
            ar: "API: الإصدار العاشر من Q1 ونطاقه الموسّع",
          })}
        </a>
      </P>
      <P>
        <a
          href="https://www.api.org/products-and-services/standards/important-standards-announcements/specq2"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "API: Q2 second edition", ar: "API: الإصدار الثاني من Q2" })}
        </a>
      </P>
      <P>
        <a
          href="https://www.api.org/products-and-services/api-monogram-and-apiqr"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "API: APIQR and Monogram programmes", ar: "API: برنامجا APIQR وMonogram" })}
        </a>
      </P>
      <P>
        <a
          href="https://www.iso.org/certification.html"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-primary underline underline-offset-4"
        >
          {t({ en: "ISO: certification and accreditation", ar: "ISO: منح الشهادات والاعتماد" })}
        </a>
      </P>
    </>
  );
}
